import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { Paper } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchMonthlyProjection, generateMonthlyProjection, fetchAffiliateProjection, generateAffiliateProjection, setMonth, setStartDate, } from "../../redux/monthlyProjectionSlice";
import PageContainer from "../../common_components/PageContainer";
import { fetchAllPages } from "../../utils/exportHelpers";
import MonthlyProjectionHeader from "./Component/MonthlyProjectionHeader";
import PaidMediaProjection from "./Component/PaidMediaProjection";
import AffiliateProjection from "./Component/AffiliateProjection";
import exportMonthlyProjection from "./ExportMonthlyProjection";
import exportAffiliateProjection from "./ExportAffiliateProjection";
/**
 * Monthly Projection module container.
 * Owns the shared calculation controls (target month, growth-rate anchor, Generate, Export),
 * the table toolbar state (search, column visibility) and the active projection-type tab.
 * Only the Paid Media tab is built today; Affiliate is scaffolded/switchable in the header.
 */
export default function MonthlyProjectionMain() {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState("paid_media");
    const [exporting, setExporting] = useState(false);
    // Table toolbar state (per tab — paid media & affiliate have different column sets).
    const [searchText, setSearchText] = useState("");
    const [committedSearch, setCommittedSearch] = useState("");
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [affHiddenColumns, setAffHiddenColumns] = useState(new Set());
    const [columnVisAnchor, setColumnVisAnchor] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({ left: [], right: [] });
    // Affiliate table is wide (21 columns) — keep Client Name pinned left by default.
    const [affPinnedColumns, setAffPinnedColumns] = useState({ left: ["client_name"], right: [] });
    const { lastUpdatedAt, loading, generating, month, startDate, page, limit, sortBy, sortOrder, affiliateLastUpdatedAt, affiliateLoading, affiliateGenerating, affiliatePage, affiliateLimit, affiliateSortBy, affiliateSortOrder, } = useSelector((state) => state.monthlyProjection);
    const isAffiliate = activeTab === "affiliate";
    // Debounce the search box so we don't refetch on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setCommittedSearch(searchText.trim()), 400);
        return () => clearTimeout(t);
    }, [searchText]);
    // Load stored (scheduler / previously generated) data for the ACTIVE tab on mount and whenever
    // the target month / paging / sort / search / tab changes. Generate has its own path below.
    useEffect(() => {
        if (isAffiliate) {
            dispatch(fetchAffiliateProjection({ month, page: affiliatePage, limit: affiliateLimit, sortBy: affiliateSortBy, sortOrder: affiliateSortOrder, search: committedSearch }));
        }
        else {
            dispatch(fetchMonthlyProjection({ month, page, limit, sortBy, sortOrder, search: committedSearch }));
        }
    }, [dispatch, isAffiliate, month, page, limit, sortBy, sortOrder, affiliatePage, affiliateLimit, affiliateSortBy, affiliateSortOrder, committedSearch]);
    const handleGenerate = useCallback(async () => {
        if (generating || affiliateGenerating)
            return;
        try {
            // A client can be BOTH paid_media and affiliate — generate both types in one action so
            // dual-type clients get a projection in each tab. Each engine only picks up the clients
            // whose type matches (paid_media / affiliate); dual-type clients match both.
            await Promise.all([
                dispatch(generateMonthlyProjection({ month, startDate, page: 1, limit, sortBy, sortOrder, search: committedSearch })).unwrap(),
                dispatch(generateAffiliateProjection({ month, startDate, page: 1, limit: affiliateLimit, sortBy: affiliateSortBy, sortOrder: affiliateSortOrder, search: committedSearch })).unwrap(),
            ]);
            // Refresh whichever tab is showing.
            if (isAffiliate) {
                dispatch(fetchAffiliateProjection({ month, page: affiliatePage, limit: affiliateLimit, sortBy: affiliateSortBy, sortOrder: affiliateSortOrder, search: committedSearch }));
            }
            else {
                dispatch(fetchMonthlyProjection({ month, page, limit, sortBy, sortOrder, search: committedSearch }));
            }
        }
        catch (err) {
            console.error("Generate projection failed:", err);
        }
    }, [dispatch, isAffiliate, generating, affiliateGenerating, month, startDate, page, limit, sortBy, sortOrder, affiliatePage, affiliateLimit, affiliateSortBy, affiliateSortOrder, committedSearch]);
    const handleRefresh = useCallback(() => {
        if (isAffiliate) {
            if (affiliateLoading)
                return;
            dispatch(fetchAffiliateProjection({ month, page: affiliatePage, limit: affiliateLimit, sortBy: affiliateSortBy, sortOrder: affiliateSortOrder, search: committedSearch }));
        }
        else {
            if (loading)
                return;
            dispatch(fetchMonthlyProjection({ month, page, limit, sortBy, sortOrder, search: committedSearch }));
        }
    }, [dispatch, isAffiliate, loading, affiliateLoading, month, page, limit, sortBy, sortOrder, affiliatePage, affiliateLimit, affiliateSortBy, affiliateSortOrder, committedSearch]);
    const handleExport = useCallback(async () => {
        if (exporting)
            return;
        setExporting(true);
        try {
            let cachedMeta = null;
            if (isAffiliate) {
                const allRows = await fetchAllPages(async (p, l) => {
                    const result = await dispatch(fetchAffiliateProjection({ month, page: p, limit: l, sortBy: affiliateSortBy, sortOrder: affiliateSortOrder, search: committedSearch })).unwrap();
                    if (!cachedMeta)
                        cachedMeta = result.meta;
                    return { items: result.rows || [], totalPages: result.pagination?.total_pages || 1 };
                });
                exportAffiliateProjection(allRows, cachedMeta);
            }
            else {
                let cachedSummary = null;
                const allRows = await fetchAllPages(async (p, l) => {
                    const result = await dispatch(fetchMonthlyProjection({ month, page: p, limit: l, sortBy, sortOrder, search: committedSearch })).unwrap();
                    if (!cachedSummary)
                        cachedSummary = result.total_summary;
                    if (!cachedMeta)
                        cachedMeta = result.meta;
                    return { items: result.rows || [], totalPages: result.pagination?.total_pages || 1 };
                });
                exportMonthlyProjection(allRows, cachedSummary, cachedMeta);
            }
        }
        catch (error) {
            console.error("Export failed:", error);
        }
        finally {
            setExporting(false);
        }
    }, [dispatch, isAffiliate, month, sortBy, sortOrder, affiliateSortBy, affiliateSortOrder, committedSearch, exporting]);
    return (_jsx(PageContainer, { children: _jsxs(Paper, { elevation: 0, sx: { display: "flex", flexDirection: "column", gap: "8px", background: "transparent" }, children: [_jsx(MonthlyProjectionHeader, { activeTab: activeTab, onTabChange: setActiveTab, lastUpdatedAt: isAffiliate ? affiliateLastUpdatedAt : lastUpdatedAt, loading: isAffiliate ? affiliateLoading : loading, onRefresh: handleRefresh, month: month, onMonthChange: (ym) => dispatch(setMonth(ym)), startDate: startDate, onStartDateChange: (ymd) => dispatch(setStartDate(ymd)), onGenerate: handleGenerate, generating: isAffiliate ? affiliateGenerating : generating, searchText: searchText, onSearchChange: setSearchText, onColumnVisibilityClick: (e) => setColumnVisAnchor(e.currentTarget), onExport: handleExport, exporting: exporting }), isAffiliate ? (_jsx(AffiliateProjection, { hiddenColumns: affHiddenColumns, onColumnVisibilityChange: setAffHiddenColumns, columnVisibilityAnchor: columnVisAnchor, onColumnVisibilityClose: () => setColumnVisAnchor(null), pinnedColumns: affPinnedColumns, onColumnPinningChange: setAffPinnedColumns })) : (_jsx(PaidMediaProjection, { hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchor: columnVisAnchor, onColumnVisibilityClose: () => setColumnVisAnchor(null), pinnedColumns: pinnedColumns, onColumnPinningChange: setPinnedColumns }))] }) }));
}
